import { useNavigate, useParams } from "@tanstack/react-router";
import { surfaceBase } from "@web/components/ui";
import { useCallback, useEffect, useState } from "react";
import { SelectField } from "@/components/ui";
import { useAuth, useToast } from "@/contexts";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import {
  addGroupMember,
  deleteGroup,
  getGroup,
  leaveGroup,
  listGroupMembers,
  listOrgGroups,
  removeGroupMember,
  updateGroup,
  updateGroupMemberRole,
} from "@/features/organizations/groups";
import type { Group, GroupMember, UserProfile } from "@/types";

export default function SettingsGroupsDetail() {
  const { groupId } = useParams({ from: "/settings/groups/$groupId" }) as {
    groupId: string;
  };
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [group, setGroup] = useState<Group | null>(null);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editType, setEditType] = useState("office");
  const [editParentId, setEditParentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [addMemberId, setAddMemberId] = useState("");
  const [showAddMember, setShowAddMember] = useState(false);

  const loadData = useCallback(
    async (orgId?: string) => {
      setLoading(true);
      try {
        const [groupResult, orgGroupsResult, membersResult] = await Promise.all([
          getGroup(groupId),
          ...(orgId ? [listOrgGroups(orgId)] : []),
          listGroupMembers(groupId),
        ]);
        if (groupResult.error) {
          toast.showAlert("Error", groupResult.error.message, "error");
        } else if (groupResult.data) {
          setGroup(groupResult.data);
          setEditName(groupResult.data.name);
          setEditSlug(groupResult.data.slug);
          setEditType(groupResult.data.type);
          setEditParentId(groupResult.data.parentId ?? "");
        }
        if (
          orgGroupsResult &&
          !Array.isArray(orgGroupsResult) &&
          "data" in orgGroupsResult &&
          orgGroupsResult.data
        ) {
          setAllGroups(orgGroupsResult.data as Group[]);
        }
        if (!Array.isArray(membersResult) && "data" in membersResult && membersResult.data) {
          setMembers(membersResult.data as GroupMember[]);
        }
        setLoading(false);
      } catch {
        setLoading(false);
      }
    },
    [groupId, toast],
  );

  useEffect(() => {
    (async () => {
      const { data, error } = await getCurrentUserProfile();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        setLoading(false);
        return;
      }
      setProfile(data);
      loadData(data?.orgId ?? undefined);
    })();
  }, [loadData, toast]);

  async function handleSaveEdit() {
    if (!editName.trim() || !editSlug.trim()) {
      toast.showAlert("Validation", "Name and slug are required.", "error");
      return;
    }
    setSaving(true);
    const { error } = await updateGroup(groupId, {
      name: editName.trim(),
      slug: editSlug.trim(),
      type: editType,
      parentId: editParentId || null,
    });
    setSaving(false);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      toast.showAlert("Saved", "Group updated.", "success");
      setEditing(false);
      loadData(profile?.orgId ?? undefined);
    }
  }

  async function handleAddMember() {
    if (!addMemberId.trim()) return;
    const { error } = await addGroupMember(groupId, addMemberId.trim());
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setAddMemberId("");
      setShowAddMember(false);
      toast.showAlert("Added", "Member added.", "success");
      loadData(profile?.orgId ?? undefined);
    }
  }

  async function handleRemoveMember(targetUserId: string) {
    toast.showConfirm("Remove member", "Remove this member from the group?", async () => {
      const { error } = await removeGroupMember(groupId, targetUserId);
      if (error) {
        toast.showAlert("Error", error.message, "error");
      } else {
        toast.showAlert("Removed", "Member removed.", "success");
        loadData(profile?.orgId ?? undefined);
      }
    });
  }

  async function handleToggleRole(member: GroupMember) {
    const newRole = member.role === "manager" ? "member" : "manager";
    const { error } = await updateGroupMemberRole(groupId, member.userId, newRole);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      loadData(profile?.orgId ?? undefined);
    }
  }

  async function handleLeave() {
    toast.showConfirm("Leave group", "Are you sure you want to leave this group?", async () => {
      const { error } = await leaveGroup(groupId);
      if (error) {
        toast.showAlert("Error", error.message, "error");
      } else {
        toast.showAlert("Left", "You left the group.", "success");
        navigate({ to: "/settings/groups" });
      }
    });
  }

  async function handleDelete() {
    toast.showConfirm(
      "Delete group",
      `Delete "${group?.name}"? This action cannot be undone.`,
      async () => {
        const { error } = await deleteGroup(groupId);
        if (error) {
          toast.showAlert("Error", error.message, "error");
        } else {
          toast.showAlert("Deleted", "Group deleted.", "success");
          navigate({ to: "/settings/groups" });
        }
      },
    );
  }

  const isMember = members.some((m) => m.userId === user?.id);
  const hasChildren = allGroups.some((g) => g.parentId === groupId);
  const parentGroup = group?.parentId ? allGroups.find((g) => g.id === group?.parentId) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              Loading...
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-background relative">
        <div className="h-screen overflow-y-auto">
          <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => navigate({ to: "/settings/groups" })}
                className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
              >
                <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
              </button>
            </div>
            <div className={`${surfaceBase} bg-card p-5 text-center`}>
              <span className="text-text-dim text-[1rem]">Group not found.</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const typeLabel =
    group.type === "office" ? "Office" : group.type === "department" ? "Department" : "Custom";

  const availableParents = allGroups.filter((g) => g.id !== groupId && !g.parentId);

  return (
    <div className="min-h-screen bg-background relative">
      <div className="h-screen overflow-y-auto">
        <div className="flex flex-col px-5 pt-3 pb-9 gap-5 md:px-5 max-w-2xl mx-auto">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate({ to: "/settings/groups" })}
              className="py-1.5 px-2.5 border border-[rgba(28,27,46,0.1)] rounded-[16px] bg-[rgba(255,255,255,0.4)] cursor-pointer"
            >
              <span className="text-text-muted text-[0.8125rem] font-bold">&larr; Back</span>
            </button>
            <span className="text-text text-[1.75rem] font-black tracking-[-0.0313em]">
              {group.name}
            </span>
            <span
              className={`${surfaceBase} bg-white px-2.5 py-1 text-[0.625rem] font-bold uppercase tracking-[0.05em]`}
            >
              {typeLabel}
            </span>
          </div>

          {parentGroup && (
            <div className="flex items-center gap-1">
              <span className="text-text-dim text-[0.75rem]">Parent:</span>
              <button
                type="button"
                onClick={() => navigate({ to: `/settings/groups/${parentGroup.id}` })}
                className="text-aqua text-[0.75rem] font-bold bg-transparent border-0 cursor-pointer"
              >
                {parentGroup.name}
              </button>
            </div>
          )}

          <div className={`${surfaceBase} bg-card p-5 flex flex-col gap-4`}>
            <div className="flex items-center justify-between">
              <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                Details
              </span>
              <button
                type="button"
                onClick={() => {
                  setEditing(!editing);
                  if (!editing) {
                    setEditName(group.name);
                    setEditSlug(group.slug);
                    setEditType(group.type);
                    setEditParentId(group.parentId ?? "");
                  }
                }}
                className="bg-[rgba(255,45,126,0.08)] rounded-[12px] py-1.5 px-3 text-aqua text-[0.75rem] font-bold border-0 cursor-pointer"
              >
                {editing ? "Cancel" : "Edit"}
              </button>
            </div>

            {editing ? (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="edit-name"
                    className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                  >
                    Name
                  </label>
                  <input
                    id="edit-name"
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="edit-slug"
                    className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                  >
                    Slug
                  </label>
                  <input
                    id="edit-slug"
                    type="text"
                    value={editSlug}
                    onChange={(e) => setEditSlug(e.target.value)}
                    className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                  />
                </div>
                <SelectField
                  label="Type"
                  value={editType}
                  options={[
                    { label: "Office", value: "office" },
                    { label: "Department", value: "department" },
                    { label: "Custom", value: "custom" },
                  ]}
                  onValueChange={(v) => setEditType(v)}
                />
                <SelectField
                  label="Parent group"
                  value={editParentId}
                  options={[
                    { label: "None", value: "" },
                    ...availableParents.map((g) => ({ label: g.name, value: g.id })),
                  ]}
                  onValueChange={(v) => setEditParentId(v)}
                />
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between py-1">
                  <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
                    Slug
                  </span>
                  <span className="text-text text-[0.875rem] font-bold">/{group.slug}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
                    Members
                  </span>
                  <span className="text-text text-[0.875rem] font-bold">{group.memberCount}</span>
                </div>
                <div className="flex items-center justify-between py-1">
                  <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
                    Type
                  </span>
                  <span className="text-text text-[0.875rem] font-bold">{typeLabel}</span>
                </div>
                {parentGroup && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]">
                      Parent
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate({ to: `/settings/groups/${parentGroup.id}` })}
                      className="text-aqua text-[0.875rem] font-bold bg-transparent border-0 cursor-pointer"
                    >
                      {parentGroup.name}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={`${surfaceBase} bg-card p-5 flex flex-col gap-3`}>
            <div className="flex items-center justify-between">
              <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
                Members
              </span>
              <button
                type="button"
                onClick={() => setShowAddMember(!showAddMember)}
                className="bg-aqua rounded-[12px] py-1.5 px-3 text-white text-[0.75rem] font-bold border border-text cursor-pointer"
              >
                Add member
              </button>
            </div>

            {showAddMember && (
              <div className="flex flex-col gap-2 p-3 bg-[rgba(0,0,0,0.03)] rounded-[12px]">
                <div className="flex flex-col gap-1.5">
                  <label
                    htmlFor="add-member-id"
                    className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
                  >
                    User ID or email
                  </label>
                  <input
                    id="add-member-id"
                    type="text"
                    value={addMemberId}
                    onChange={(e) => setAddMemberId(e.target.value)}
                    placeholder="user-id"
                    className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddMember}
                    className="bg-aqua rounded-[12px] py-2 px-4 text-white text-[0.75rem] font-extrabold border border-text cursor-pointer"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMember(false);
                      setAddMemberId("");
                    }}
                    className="bg-white rounded-[12px] py-2 px-4 text-text-muted text-[0.75rem] font-extrabold border border-text cursor-pointer"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {members.length === 0 && (
              <p className="text-text-muted text-[0.875rem] italic">
                No members in this group yet.
              </p>
            )}

            {members.map((member) => (
              <div
                key={member.userId}
                className="flex items-center gap-3 py-2 border-b border-[rgba(28,27,46,0.06)] last:border-b-0"
              >
                <div className="w-8 h-8 rounded-full bg-[rgba(255,45,126,0.1)] flex items-center justify-center shrink-0">
                  <span className="text-aqua text-xs font-extrabold">
                    {member.displayName?.charAt(0)?.toUpperCase() ?? "?"}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-text text-[0.875rem] font-bold block truncate">
                    {member.displayName}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleToggleRole(member)}
                  className={`rounded-[12px] py-1 px-2.5 text-[0.625rem] font-extrabold border border-text uppercase cursor-pointer ${
                    member.role === "manager"
                      ? "bg-aqua text-white"
                      : "bg-[rgba(255,45,126,0.08)] text-aqua"
                  }`}
                >
                  {member.role}
                </button>
                <button
                  type="button"
                  onClick={() => handleRemoveMember(member.userId)}
                  className="bg-white rounded-[12px] py-1 px-2.5 text-coral text-[0.625rem] font-extrabold border border-text cursor-pointer"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            {isMember && (
              <button
                type="button"
                onClick={handleLeave}
                className="bg-white rounded-[16px] py-3 px-5 text-coral text-[0.875rem] font-extrabold border border-text cursor-pointer"
              >
                Leave group
              </button>
            )}
            {!hasChildren && (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-coral rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer disabled:opacity-50"
              >
                Delete group
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
